import React, { useState } from "react";
import { Calendar, Zap, X } from "lucide-react";

export type ModalType = "trial" | "demo" | "call";

export interface ContactFormData {
  fullName: string;
  email: string;
  phone: string;
  companyName: string;
  useCase: string;
  type: ModalType;
}

interface ContactModalProps {
  isOpen: boolean;
  type: ModalType;
  onClose: () => void;
  onSubmit: (data: ContactFormData) => void;
}

const ContactModal: React.FC<ContactModalProps> = ({
  isOpen,
  type,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    companyName: "",
    useCase: "Alternative Lender",
  });

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  if (!isOpen) return null;

  const getModalConfig = () => {
    switch (type) {
      case "trial":
        return {
          title: "Start Free Trial",
          description: "Start your free trial and explore our platform",
          buttonText: "Start Free Trial",
          buttonIcon: <Zap className="w-5 h-5" />,
        };
      case "demo":
        return {
          title: "Schedule a Demo",
          description: "See Crypsey in action with a personalized demo",
          buttonText: "Schedule Demo",
          buttonIcon: <Calendar className="w-5 h-5" />,
        };
      case "call":
        return {
          title: "Schedule a Call",
          description: "Let's discuss how Crypsey can help your business",
          buttonText: "Schedule Call",
          buttonIcon: <Calendar className="w-5 h-5" />,
        };
    }
  };

  const config = getModalConfig();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeInUp">
      <div className="glassmorphic-strong rounded-3xl p-8 max-w-md w-full relative">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-white transition-colors hover:rotate-90 duration-300"
        >
          <X className="w-6 h-6" />
        </button>
        <h3 className="text-3xl font-bold mb-2">{config.title}</h3>
        <p className="text-gray-400 mb-8">{config.description}</p>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit({
              ...formData,
              type,
            });
          }}
        >
          <div>
            <label className="block text-sm font-medium mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 bg-[#0A0E27] border border-gray-700 rounded-xl focus:border-cyan-400 focus:outline-none transition-colors"
              placeholder="John Doe"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Email *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-3 bg-[#0A0E27] border border-gray-700 rounded-xl focus:border-cyan-400 focus:outline-none transition-colors"
              placeholder="john@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Phone</label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A0E27] border border-gray-700 rounded-xl focus:border-cyan-400 focus:outline-none transition-colors"
              placeholder="+1 (555) 123-4567"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">
              Company Name
            </label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A0E27] border border-gray-700 rounded-xl focus:border-cyan-400 focus:outline-none transition-colors"
              placeholder="Your Company"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Use Case</label>
            <select
              name="useCase"
              value={formData.useCase}
              onChange={handleInputChange}
              className="w-full px-4 py-3 bg-[#0A0E27] border border-gray-700 rounded-xl focus:border-cyan-400 focus:outline-none transition-colors"
            >
              <option>Alternative Lender</option>
              <option>PropTech Company</option>
              <option>Mortgage Fund</option>
              <option>Other</option>
            </select>
          </div>
          <button
            type="submit"
            className="w-full btn-font bg-gradient-to-r from-cyan-500 to-purple-500 px-8 py-4 rounded-xl font-bold text-lg hover-glow flex items-center justify-center gap-2"
          >
            {config.buttonIcon}
            {config.buttonText}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactModal;
